import { Box, Card, styled, Button, TextField, MenuItem, Typography, CircularProgress, FormControl, InputLabel, Select, Pagination } from "@mui/material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { useEffect, useState, useCallback, useMemo } from "react";
import FieldVisitTable from "page-sections/FieldVisit/FieldVisitTable";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { GetApp, ArrowForward } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import FieldVisitExportModal from "page-sections/FieldVisit/FieldVisitExportModal";

const FieldVisit = () => {
    const { setPageTitle } = usePageTitle();
    const navigate = useNavigate();
    const [fieldVisits, setFieldVisits] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [companyFilter, setCompanyFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [totalVisits, setTotalVisits] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        page_size: 10,
        total_items: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
        next_page_number: null,
        previous_page_number: null,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [10, 25, 50, 100];

    useEffect(() => {
        setPageTitle('Field Visit');
    }, [setPageTitle]);

    // Check if user is Company type
    const isCompanyUser = apiService.isCompanyUser();
    const companyId = apiService.getCompanyId();

    // Fetch companies (for admin users only)
    const fetchCompanies = useCallback(async () => {
        if (isCompanyUser) {
            // For company users, use their own company
            const details = apiService.getCompanyDetails();
            const id = apiService.getCompanyId();
            const singleCompany = {
                id: id,
                name: details?.name || "My Company",
            };
            setCompanyOptions([singleCompany]);
            setCompanyFilter(id?.toString() || "");
            return;
        }

        setLoadingCompanies(true);
        try {
            const response = await apiService.get(API_ENDPOINTS.GET_company_choices);
            const options = response?.data?.Company || response?.data?.choices || response?.data || [];
            if (Array.isArray(options)) {
                setCompanyOptions(options);
            } else {
                setCompanyOptions([]);
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
            setCompanyOptions([]);
        } finally {
            setLoadingCompanies(false);
        }
    }, [isCompanyUser]);

    // Fetch categories (product categories) from API based on selected company
    const fetchCategories = useCallback(async (selectedCompanyId = null) => {
        setLoadingCategories(true);
        try {
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            
            if (!targetCompanyId) {
                setCategories([]);
                return;
            }

            // Fetch categories for the selected company
            const userId = localStorage.getItem('user_id');
            const response = await apiService.get(`${API_ENDPOINTS.GET_product_category}?company=${targetCompanyId}&user_id=${userId}`);
            
            if (response?.success) {
                const categoryList = response.data?.ProductCategory || [];
                setCategories(categoryList);
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            setCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    }, [isCompanyUser, companyId]);

    // Fetch field visits with company_id and pagination
    const fetchFieldVisits = useCallback(async (selectedCompanyId = null, page = 1, pageSizeParam = 10) => {
        setLoading(true);
        try {
            let url = API_ENDPOINTS.GET_field_visit;
            const params = new URLSearchParams();
            params.set('page', String(page));
            params.set('page_size', String(pageSizeParam));
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            if (targetCompanyId) params.set('company_id', targetCompanyId);
            url += (url.includes('?') ? '&' : '?') + params.toString();

            const response = await apiService.get(url);

            if (response?.success && response?.data?.FieldVisit) {
                setFieldVisits(response.data.FieldVisit);
            } else {
                setFieldVisits([]);
            }
            const paginationData = response?.pagination || response?.data?.pagination || {};
            if (Object.keys(paginationData).length > 0) {
                setPagination({
                    current_page: paginationData.current_page ?? page,
                    page_size: paginationData.page_size ?? pageSizeParam,
                    total_items: paginationData.total_items ?? 0,
                    total_pages: paginationData.total_pages ?? 1,
                    has_next: paginationData.has_next ?? false,
                    has_previous: paginationData.has_previous ?? false,
                    next_page_number: paginationData.next_page_number ?? null,
                    previous_page_number: paginationData.previous_page_number ?? null,
                });
                setCurrentPage(paginationData.current_page ?? page);
                setPageSize(paginationData.page_size ?? pageSizeParam);
            } else {
                const list = response?.data?.FieldVisit || [];
                setPagination(prev => ({ ...prev, total_items: list.length, total_pages: 1, current_page: 1, page_size: pageSizeParam }));
            }
        } catch (error) {
            console.error("API error:", error);
            setFieldVisits([]);
        } finally {
            setLoading(false);
        }
    }, [isCompanyUser, companyId]);

    const handleFieldVisitPageChange = (_, value) => {
        setCurrentPage(value);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, value, pageSize);
    };

    const handleFieldVisitPageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setCurrentPage(1);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, 1, newSize);
    };

    // Fetch dashboard data for total visits
    const fetchDashboardData = useCallback(async () => {
        setLoadingDashboard(true);
        try {
            // Get current date range (last year to next month for default range)
            const today = new Date();
            const dateFrom = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0]; // Jan 1 of current year
            const dateTo = new Date(today.getFullYear() + 1, 0, 31).toISOString().split('T')[0]; // Jan 31 of next year
            
            const url = `${API_ENDPOINTS.GET_field_visit_dashboard}?user_type=agent`;
            const response = await apiService.get(url);
            
            if (response?.success && response?.data?.summary) {
                setTotalVisits(response.data.summary.total_visits);
            } else {
                setTotalVisits(null);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setTotalVisits(null);
        } finally {
            setLoadingDashboard(false);
        }
    }, []);

    // Fetch companies on component mount
    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    // Fetch categories when company changes
    useEffect(() => {
        if (companyFilter || (isCompanyUser && companyId)) {
            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
            fetchCategories(targetCompanyId);
            // Reset category filter when company changes
            setCategoryFilter("");
        } else {
            setCategories([]);
            setCategoryFilter("");
        }
    }, [companyFilter, isCompanyUser, companyId, fetchCategories]);

    // Fetch field visits on component mount and when company changes
    useEffect(() => {
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchFieldVisits(targetCompanyId, currentPage, pageSize);
    }, [companyFilter, isCompanyUser, companyId, fetchFieldVisits]);

    // Fetch dashboard data on component mount
    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Handle company filter change
    const handleCompanyFilterChange = (e) => {
        setCompanyFilter(e.target.value);
    };

    // Handle category filter change
    const handleCategoryFilterChange = (e) => {
        setCategoryFilter(e.target.value);
        // When category is selected, refresh with company_id
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        if (targetCompanyId) {
            fetchFieldVisits(targetCompanyId, currentPage, pageSize);
        }
    };

    // Export handlers
    const handleExportClick = () => {
        setExportModalOpen(true);
    };

    const handleExportModalClose = () => {
        setExportModalOpen(false);
    };

    return (
        <Box pt={2} pb={4}>
            <FlexBetween flexWrap="wrap" mb={2}>
                <FlexBox alignItems="center" gap={2} style={{ flexGrow: 1 }}>
                    <SearchInput
                        bordered={false}
                        placeholder="Search by farmer name, village, executive, product, SKU..."
                        onChange={e => setSearchValue(e.target.value)}
                        value={searchValue}
                              sx={{
                        width: "45%",
                        maxWidth: "none !important"
                    }}
                    />
                </FlexBox>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {/* <Button
                        variant="outlined"
                        size="small"
                        endIcon={<ArrowForward />}
                        onClick={() => navigate('/dashboard/disease-advisory')}
                        sx={{ textTransform: 'none' }}
                    >
                        Disease Advisory
                    </Button> */}
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                        <InputLabel>Rows</InputLabel>
                        <Select value={pageSize} label="Rows" onChange={handleFieldVisitPageSizeChange}>
                            {pageSizeOptions.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<GetApp />}
                        onClick={handleExportClick}
                        sx={{ textTransform: 'none' }}
                    >
                        Export to Excel
                    </Button>
                </Box>
            </FlexBetween>

            {/* Filter by Company (admin users only) */}
            {!isCompanyUser && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <Typography variant="body2" fontWeight={600}>
                        Filter by category:
                    </Typography>

                    <TextField
                        select
                        size="small"
                        label="All Category"
                        value={companyFilter}
                        onChange={handleCompanyFilterChange}
                        disabled={loading || loadingCompanies}
                        sx={{ minWidth: { xs: "100%", sm: 220 } }}
                    >
                        <MenuItem value="">All Company</MenuItem>
                        {loadingCompanies ? (
                            <MenuItem disabled>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                Loading companies...
                            </MenuItem>
                        ) : (
                            companyOptions.map((company) => (
                                <MenuItem key={company.id} value={company.id}>
                                    {company.name}
                                </MenuItem>
                            ))
                        )}
                    </TextField>

                    {/* Total Visits Display - Next to filter */}
                   {totalVisits !== null && (
                    <Box
                        sx={{
                        minWidth: { xs: "100%", sm: 220 },
                        height: "40px",
                        display: "flex",
                        alignItems: "center",     // vertical center
                        // justifyContent: "center", // horizontal center
                        paddingLeft:  "10px",
                        backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.02)",
                        borderRadius: 1,
                        border: (theme) =>
                            `1px solid ${
                            theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.23)"
                                : "rgba(0, 0, 0, 0.23)"
                            }`,
                        }}
                    >
                        <Typography
                        variant="body1"
                        // fontWeight={600}
                        color="text.primary"
                        >
                        Total Visits: {totalVisits.toLocaleString()}
                        </Typography>
                    </Box>
                    )}

                </Box>
            )}

            {/* Total Visits Display - For company users (when filter is not shown) */}
            {isCompanyUser && totalVisits !== null && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <Typography variant="body2" fontWeight={600}>
                        Filter by category:
                    </Typography>
                    <Box
                        sx={{
                            position: "relative",
                            minWidth: { xs: "100%", sm: 220 },
                            height: "56px", // Match TextField size="small" with label height
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-end",
                            px: 1.5, // Match TextField padding
                            pb: 1.25, // Match TextField bottom padding
                            pt: 2, // Space for label
                            backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.05)' 
                                : 'rgba(0, 0, 0, 0.02)',
                            borderRadius: 1,
                            border: (theme) => `1px solid ${theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.23)' 
                                : 'rgba(0, 0, 0, 0.23)'}`,
                        }}
                    >
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                                position: "absolute",
                                top: "9px",
                                left: "14px",
                                fontSize: "0.75rem",
                                lineHeight: 1,
                            }}
                        >
                            Total Visits
                        </Typography>
                        {loadingDashboard ? (
                            <CircularProgress size={16} />
                        ) : (
                            <Typography 
                                variant="body2" 
                                fontWeight={600} 
                                color="primary.main" 
                                sx={{ lineHeight: 1.4375 }}
                            >
                                {totalVisits.toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            {/* Filter by Category */}
            {/* <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 2,
                }}
            >
                <Typography variant="body2" fontWeight={600}>
                    Filter by category:
                </Typography>

                <TextField
                    select
                    size="small"
                    label="All Category"
                    value={categoryFilter}
                    onChange={handleCategoryFilterChange}
                    disabled={loading || loadingCategories || (!companyFilter && !isCompanyUser)}
                    sx={{ minWidth: { xs: "100%", sm: 220 } }}
                >
                    <MenuItem value="">All Category</MenuItem>
                    {loadingCategories ? (
                        <MenuItem disabled>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Loading categories...
                        </MenuItem>
                    ) : (
                        categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                                {category.name}
                        </MenuItem>
                        ))
                    )}
                </TextField>
            </Box> */}

            <Card>
                <Box sx={{ mt: 1 }}>
                    <FieldVisitTable
                        fieldVisits={fieldVisits}
                        searchQuery={searchValue}
                        loading={loading}
                        onRefresh={() => {
                            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                            fetchFieldVisits(targetCompanyId, currentPage, pageSize);
                            fetchDashboardData(); // Refresh dashboard data on manual refresh
                        }}
                        pagination={pagination}
                        onPageChange={handleFieldVisitPageChange}
                        onPageSizeChange={handleFieldVisitPageSizeChange}
                        pageSizeOptions={pageSizeOptions}
                    />
                </Box>
            </Card>

            <FieldVisitExportModal
                open={exportModalOpen}
                onClose={handleExportModalClose}
                onRefresh={() => {
                    const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                    fetchFieldVisits(targetCompanyId, currentPage, pageSize);
                }}
            />
        </Box>
    );
};

export default FieldVisit;

